/**
 * Pure TEA update for the export confirmation toast.
 *
 * A desktop save offers "Show in folder" and "Open". A save on the
 * Android/iOS shell names the file but offers no actions (the opener plugin
 * cannot reveal files there and the app has no open-path grant on mobile).
 * A web download confirms the file left the app with no desktop actions.
 * Shell: `useExportToast.ts` (opener plugin Cmds via `dispatchInto`).
 */
import { assertNever } from "../lib/assertNever";
import { just, matchMaybe, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type ExportSuccessTarget =
  | { readonly kind: "desktop"; readonly path: string }
  | { readonly kind: "device"; readonly path: string }
  | { readonly kind: "download" };

export type ExportNotice =
  | { readonly kind: "hidden" }
  | { readonly kind: "info"; readonly message: string }
  | { readonly kind: "error"; readonly message: string }
  | {
      readonly kind: "success";
      readonly message: string;
      readonly target: ExportSuccessTarget;
    };

export type ExportToastModel = {
  readonly notice: ExportNotice;
};

export const initialExportToastModel: ExportToastModel = {
  notice: { kind: "hidden" },
};

export type ExportToastMsg =
  | {
      readonly type: "Saved";
      readonly format: string;
      readonly target: ExportSuccessTarget;
    }
  | { readonly type: "Info"; readonly message: string }
  | { readonly type: "Failed"; readonly message: string }
  | { readonly type: "Dismiss" }
  | { readonly type: "ShowInFolder" }
  | { readonly type: "Open" }
  | { readonly type: "ActionFailed"; readonly message: string };

export type ExportToastCmd =
  | { readonly kind: "revealInFolder"; readonly path: string }
  | { readonly kind: "openPath"; readonly path: string };

export type ExportToastButton = {
  readonly id: "showInFolder" | "open";
  readonly label: string;
  readonly primary: boolean;
};

function exportErrorDetail(error: unknown): string {
  if (typeof error === "string") return error.trim();
  if (error instanceof Error) return error.message.trim();
  return "";
}

/**
 * Failure copy for the export toast: what failed, then the reason when the
 * error carries one (`Could not export EPUB. Permission denied (os error 13)`),
 * so a bare OS error never shows without saying which export it was.
 */
export function formatExportFailure(summary: string, error: unknown): string {
  const detail = exportErrorDetail(error);
  if (detail.length === 0) return summary;
  if (summary.length === 0) return detail;
  return `${summary} ${detail}`;
}

function exportFileLabel(path: string): string {
  const normalized = path.replace(/\\/g, "/");
  const slash = normalized.lastIndexOf("/");
  const name = slash >= 0 ? normalized.slice(slash + 1) : normalized;
  return name.length > 0 ? name : path;
}

function savedNotice(
  format: string,
  target: ExportSuccessTarget,
): ExportNotice {
  switch (target.kind) {
    case "download":
      return {
        kind: "success",
        message: `Downloaded ${format}.`,
        target,
      };
    case "desktop":
    case "device": {
      const path = target.path.trim();
      if (path.length === 0) {
        return {
          kind: "error",
          message: `Could not export ${format}.`,
        };
      }
      return {
        kind: "success",
        message: `Exported ${exportFileLabel(path)}.`,
        target: { kind: target.kind, path },
      };
    }
    default:
      return assertNever(target);
  }
}

function desktopPath(notice: ExportNotice): Maybe<string> {
  if (notice.kind !== "success" || notice.target.kind !== "desktop") {
    return nothing;
  }
  return just(notice.target.path);
}

export function exportToastMessage(notice: ExportNotice): string {
  switch (notice.kind) {
    case "hidden":
      return "";
    case "info":
    case "error":
    case "success":
      return notice.message;
    default:
      return assertNever(notice);
  }
}

/** Desktop saves can reveal or open the file. Mobile saves, downloads and errors cannot. */
export function exportToastButtons(
  notice: ExportNotice,
): readonly ExportToastButton[] {
  if (notice.kind !== "success" || notice.target.kind !== "desktop") return [];
  return [
    { id: "showInFolder", label: "Show in folder", primary: true },
    { id: "open", label: "Open", primary: false },
  ];
}

export function updateExportToast(
  model: ExportToastModel,
  msg: ExportToastMsg,
): readonly [ExportToastModel, readonly ExportToastCmd[]] {
  switch (msg.type) {
    case "Saved":
      return [{ notice: savedNotice(msg.format, msg.target) }, []];
    case "Info":
      return [{ notice: { kind: "info", message: msg.message } }, []];
    case "Failed":
    case "ActionFailed":
      return [{ notice: { kind: "error", message: msg.message } }, []];
    case "Dismiss":
      return [initialExportToastModel, []];
    case "ShowInFolder":
      return matchMaybe(desktopPath(model.notice), {
        nothing: () => [model, []],
        just: (path) => [model, [{ kind: "revealInFolder", path }]],
      });
    case "Open":
      return matchMaybe(desktopPath(model.notice), {
        nothing: () => [model, []],
        just: (path) => [model, [{ kind: "openPath", path }]],
      });
    default:
      return assertNever(msg);
  }
}

export const updateExportToastFn: UpdateFn<
  ExportToastModel,
  ExportToastMsg,
  ExportToastCmd
> = updateExportToast;
